import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { KeyRound, ListChecks, Loader2, Play } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import type { RestoreBridge } from '../bridge'
import type { RealRun } from '../useSelfTest'

/**
 * A real restore, from Orchestrate (Andrew, 15 Oct 2026: "a real restore through Orchestrate ->
 * plug-in -> daemon -> vsrecovery, on his Mac", smallest first: a raw image to a file).
 *
 * Everything on this form comes from the agent itself - `restoreCapabilities`, the agent's
 * capabilities document (vsrecovery-integration-8, docs/schema/capabilities.schema.json):
 * the commands this machine supports, each command's inputs (name with its dashes, type,
 * required, default, meaning) and the names of the secrets it takes. Nothing about a command is
 * written into this screen.
 *
 * The secrets the engineer types here go only to the plug-in, beside the signed job, and are
 * cleared from the form as soon as the run is sent (Andrew, 15 Oct 2026). They never reach
 * Orchestrate's back end.
 */
/** kind: flag (a switch), value, repeatable, or refused (the agent will not take it here). */
export interface CapabilityInput { name: string; kind?: 'flag' | 'value' | 'repeatable' | 'refused'; type: string; required: string; secret: boolean; default: string; meaning: string }
export interface CapabilitySecret { name: string; required: string; meaning: string; delivery?: string }
export interface CapabilityCommand {
    name: string
    summary: string
    execute_switch?: string
    inputs: CapabilityInput[]
    secrets: CapabilitySecret[]
    steps: Array<{ id: string; description: string; changes_target: boolean }>
    supported_here: boolean
    this_host?: string
}
export interface Capabilities { agent_version: string; commands: CapabilityCommand[] }

/**
 * A job may carry a write rate limit for restore-raw-image only (the Bridge document, section 3a:
 * "rateLimitBytesPerSecond - Optional. restore-raw-image only"; the back end refuses it on others).
 * Andrew, 15 Oct 2026: to slow the write enough to cancel it part way, as a test.
 */
export const RATE_LIMITED = new Set(['restore-raw-image'])
export const MB = 1024 * 1024

/** Our word for the kind of action (the Bridge document, section 3a): a command with any step that changes its target restores. */
export const operationClassOf = (command: CapabilityCommand): RealRun['operationClass'] => (command.steps.some((s) => s.changes_target) ? 'restore' : 'read')
const isRequired = (value: string) => /^(yes|true|always|required)\b/i.test(String(value).trim())
const isNumber = (type: string) => /^(integer|int|number|float)$/i.test(type)
const isFlag = (input: CapabilityInput) => input.kind === 'flag' || /^(boolean|bool|flag|switch)$/i.test(input.type)

/** The options to send: only what was filled in, typed as the agent declares. */
export function optionsFrom(command: CapabilityCommand, values: Record<string, string | boolean>): Record<string, string | number | boolean> {
    const out: Record<string, string | number | boolean> = {}
    for (const input of command.inputs) {
        if (input.secret || input.kind === 'refused' || !input.name.startsWith('--')) continue
        const value = values[input.name]
        if (value === undefined || value === '' || value === false) continue
        if (isFlag(input)) out[input.name] = value === true
        else if (isNumber(input.type)) { const n = Number(value); if (Number.isFinite(n)) out[input.name] = n }
        else out[input.name] = String(value)
    }
    return out
}

export function RealRestore({ bridge, sessionToken, busy, onStart }: { bridge: RestoreBridge; sessionToken: string; busy: boolean; onStart: (run: RealRun) => Promise<string | null> }) {
    const { t } = useTranslation()
    const [capabilities, setCapabilities] = useState<Capabilities | null>(null)
    const [loading, setLoading] = useState(false)
    const [problem, setProblem] = useState<string | null>(null)
    const [commandName, setCommandName] = useState('')
    const [targetRef, setTargetRef] = useState('')
    const [values, setValues] = useState<Record<string, string | boolean>>({})
    // The typed secrets: in this component's memory only, and emptied once the run is sent.
    const [secrets, setSecrets] = useState<Record<string, string>>({})
    const [rateMb, setRateMb] = useState('')

    const commands = useMemo(() => (capabilities?.commands ?? []).filter((c) => c.supported_here && c.name !== 'self-test'), [capabilities])
    const command = commands.find((c) => c.name === commandName) ?? null
    const options = command ? command.inputs.filter((i) => !i.secret && i.kind !== 'refused' && i.name.startsWith('--')) : []

    const load = async () => {
        setLoading(true)
        setProblem(null)
        try {
            const reply = await bridge.request({ type: 'restoreCapabilities' }, sessionToken, 30000)
            setCapabilities(reply.data as Capabilities)
        } catch (error) { setProblem((error as Error).message) } finally { setLoading(false) }
    }
    const missing = command ? [
        ...options.filter((i) => isRequired(i.required) && !isFlag(i) && !String(values[i.name] ?? '').trim()).map((i) => i.name),
        ...command.secrets.filter((s) => isRequired(s.required) && !secrets[s.name]).map((s) => s.name),
        ...(targetRef.trim() ? [] : [t('restoreAgent.real.target', 'Target')]),
    ] : []

    const start = async () => {
        if (!command) return
        // Only the secrets the command takes and the engineer filled in: exactly the job's list.
        const typed = Object.fromEntries(command.secrets.map((s) => [s.name, secrets[s.name] ?? '']).filter(([, v]) => v !== ''))
        const rate = Number(rateMb)
        const rateLimitBytesPerSecond = RATE_LIMITED.has(command.name) && rateMb.trim() && Number.isFinite(rate) && rate > 0 ? Math.round(rate * MB) : undefined
        const started = await onStart({ command: command.name, operationClass: operationClassOf(command), targetRef: targetRef.trim(), options: optionsFrom(command, values), secrets: typed, ...(rateLimitBytesPerSecond ? { rateLimitBytesPerSecond } : {}) })
        // Cleared whether or not it started: a value typed once is not kept for another try.
        setSecrets({})
        if (started) setValues({})
    }

    return (
        <section className="rounded-lg border p-4" data-testid="restore-real">
            <h2 className="flex items-center gap-1.5 text-base font-semibold"><ListChecks className="h-4 w-4" />{t('restoreAgent.real.title', 'A real restore')}</h2>
            <p className="mt-1 max-w-3xl text-sm text-muted-foreground">{t('restoreAgent.real.help', 'The commands and their options are what the restore agent on this machine says it can do. The job is signed by Orchestrate; any password or key you type goes only to the plug-in and is cleared once the run is sent.')}</p>
            {!capabilities ? (
                <Button className="mt-3" variant="outline" onClick={load} disabled={!sessionToken || loading} data-testid="restore-real-load">
                    {loading ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <ListChecks className="mr-1.5 h-4 w-4" />}
                    {t('restoreAgent.real.load', 'Ask the agent what it can do')}
                </Button>
            ) : (
                <div className="mt-3 space-y-3">
                    <p className="text-xs text-muted-foreground">{t('restoreAgent.real.agent', 'Restore agent {{version}} · {{count}} commands on this machine', { version: capabilities.agent_version, count: commands.length })}</p>
                    <div className="flex flex-wrap gap-3">
                        <Select value={commandName} onValueChange={(v) => { setCommandName(v); setValues({}); setSecrets({}); setRateMb('') }}>
                            <SelectTrigger className="w-72" aria-label={t('restoreAgent.real.command', 'Command')} data-testid="restore-real-command"><SelectValue placeholder={t('restoreAgent.real.chooseCommand', 'Choose a command')} /></SelectTrigger>
                            <SelectContent>{commands.map((c) => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
                        </Select>
                        <label className="flex min-w-0 flex-1 basis-64 flex-col text-sm font-medium">
                            {t('restoreAgent.real.target', 'Target')}
                            <Input className="mt-1" value={targetRef} maxLength={250} onChange={(e) => setTargetRef(e.target.value)} placeholder={t('restoreAgent.real.targetExample', 'For example: andrew-mac-file')} data-testid="restore-real-target" />
                        </label>
                    </div>
                    {command && (
                        <>
                            <p className="text-sm">{command.summary}</p>
                            <p className="text-xs text-muted-foreground">{operationClassOf(command) === 'restore' ? t('restoreAgent.real.changes', 'This command changes its target.') : t('restoreAgent.real.reads', 'This command does not change a target.')}</p>
                            <div className="grid gap-3 sm:grid-cols-2" data-testid="restore-real-options">
                                {options.map((input) => (
                                    <label key={input.name} className="flex flex-col text-sm" title={input.meaning}>
                                        <span className="font-mono text-xs font-semibold">{input.name}{isRequired(input.required) && <span className="text-destructive"> *</span>}</span>
                                        {isFlag(input) ? (
                                            <span className="mt-1 flex items-center gap-2"><input type="checkbox" checked={values[input.name] === true} onChange={(e) => setValues((v) => ({ ...v, [input.name]: e.target.checked }))} />
                                                <span className="text-xs text-muted-foreground">{input.meaning}</span></span>
                                        ) : (
                                            <>
                                                <Input className="mt-1 h-8" inputMode={isNumber(input.type) ? 'numeric' : undefined} value={String(values[input.name] ?? '')} placeholder={input.default || ''} onChange={(e) => setValues((v) => ({ ...v, [input.name]: e.target.value }))} aria-label={input.name} />
                                                <span className="mt-0.5 text-xs text-muted-foreground">{input.meaning}</span>
                                            </>
                                        )}
                                    </label>
                                ))}
                            </div>
                            {RATE_LIMITED.has(command.name) && (
                                <label className="flex max-w-xs flex-col text-sm" data-testid="restore-real-rate">
                                    <span className="font-medium">{t('restoreAgent.real.rate', 'Write speed limit (MB a second)')}</span>
                                    <Input className="mt-1 h-8" inputMode="decimal" value={rateMb} onChange={(e) => setRateMb(e.target.value.replace(/[^0-9.]/g, ''))} placeholder={t('restoreAgent.real.rateNone', 'No limit')} aria-label={t('restoreAgent.real.rate', 'Write speed limit (MB a second)')} />
                                    <span className="mt-0.5 text-xs text-muted-foreground">{t('restoreAgent.real.rateHelp', 'Slows the write, for example to have time to cancel it part way.')}</span>
                                </label>
                            )}
                            {command.secrets.length > 0 && (
                                <div className="space-y-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-3" data-testid="restore-real-secrets">
                                    <p className="flex items-center gap-1.5 text-sm font-semibold"><KeyRound className="h-4 w-4" />{t('restoreAgent.real.secrets', 'Passwords and keys for this run')}</p>
                                    <p className="text-xs text-muted-foreground">{t('restoreAgent.real.secretsHelp', 'Sent with this run to the plug-in only. Not saved by Orchestrate, and cleared from this form once the run is sent.')}</p>
                                    {command.secrets.map((secret) => (
                                        <label key={secret.name} className="flex flex-col text-sm">
                                            <span className="font-mono text-xs font-semibold">{secret.name}{isRequired(secret.required) && <span className="text-destructive"> *</span>}</span>
                                            {/key/i.test(secret.name) && /ssh/i.test(secret.name)
                                                ? <textarea className="mt-1 h-24 rounded-md border bg-background p-2 font-mono text-xs" autoComplete="off" spellCheck={false} value={secrets[secret.name] ?? ''} onChange={(e) => setSecrets((s) => ({ ...s, [secret.name]: e.target.value }))} aria-label={secret.name} />
                                                : <Input className="mt-1 h-8" type="password" autoComplete="new-password" value={secrets[secret.name] ?? ''} onChange={(e) => setSecrets((s) => ({ ...s, [secret.name]: e.target.value }))} aria-label={secret.name} />}
                                            <span className="mt-0.5 text-xs text-muted-foreground">{secret.meaning}</span>
                                        </label>
                                    ))}
                                </div>
                            )}
                            {missing.length > 0 && <p className="text-xs text-amber-700 dark:text-amber-400">{t('restoreAgent.real.missing', 'Still needed: {{list}}', { list: missing.join(', ') })}</p>}
                            <Button onClick={start} disabled={busy || missing.length > 0} data-testid="restore-real-start">
                                {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Play className="mr-1.5 h-4 w-4" />}
                                {t('restoreAgent.real.start', 'Plan and start')}
                            </Button>
                        </>
                    )}
                </div>
            )}
            {problem && <p role="alert" className="mt-2 text-sm font-medium text-destructive">{problem}</p>}
        </section>
    )
}
